import { clsx } from 'clsx';
import capitalize from 'lodash/capitalize';
import React, { useCallback, useRef, useState } from 'react';
import { useHover } from 'react-aria';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import AddCompanyModal from '~/components/@sibill-components/company/AddCompanyModal';
import ConfirmDialog from '~/components/@sibill-components/confirmation/ConfirmDialog';
import Button from '~/components/buttons/NewButton';
import { VStack } from '~/components/layout/Stack';
import Text from '~/components/typographies/Text';
import { snack } from '~/hooks/ToastProvider';
import { useActiveCompany, useSwitchCompany } from '~/hooks/useActiveCompany';
import { useAnalytics } from '~/hooks/useAnalytics';
import useAuth from '~/hooks/useAuth';
import useExceptionLogger from '~/hooks/useExceptionLogger';
import { useHasAutomaticSecondCompanyOnboarding } from '~/hooks/useFeatureFlag';
import { useOnClickOutside } from '~/hooks/useOnClickOutside';
import IconLogout from '~/icons/icon-logout.svg?react';
import IconPadlock from '~/icons/icon-padlock-closed-fill.svg?react';
import IconPlus from '~/icons/icon-plus.svg?react';
import CompanyAvatar from '~/layouts/dashboard/CompanyAvatar';
import { SubmenuContentContainer } from '~/layouts/dashboard/navigation/NavigationSidebarItem';
import subMenuStyles from '~/layouts/dashboard/navigation/NavigationSidebarItem.module.scss';
import { useModal } from '~/modals/Modal';
import { RouteConfig } from '~/routes/routeConfig';
import { AnalyticsEvents } from '~/services/analytics';
import { useLoggedUser } from '~/services/user';
import { Company, CompanyUserStatus } from '~/types/company';
import { UserCompany } from '~/types/user';
import styles from './SidebarCompany.module.scss';

interface CurrentCompanyProps {
  isActive: boolean;
}

const CurrentCompany: React.FC<CurrentCompanyProps> = ({ isActive }) => {
  const company = useActiveCompany();
  const { hoverProps, isHovered } = useHover({});

  const computedContainerClassname = clsx(styles.currentCompanyInnerContainer, {
    [styles.hovered!]: isHovered,
    [styles.active!]: isActive,
  });

  return (
    <div className={styles.currentCompanyContainer}>
      <div className={computedContainerClassname} {...hoverProps}>
        <CompanyAvatar company={company} />
      </div>
    </div>
  );
};

const SelectableCompanyAutomaticOnboardingSecondCompany: React.FC<{
  company: UserCompany;
  handleCloseSubmenu: () => void;
}> = ({ company, handleCloseSubmenu }) => {
  const { hoverProps, isHovered } = useHover({});
  const switchCompany = useSwitchCompany();
  const { t } = useTranslation();

  const handleCompanySelection = useCallback(
    (company: Company) => {
      switchCompany(company, RouteConfig.AppRoot.buildLink());
    },
    [switchCompany],
  );

  const handleClick = useCallback(
    (isDisabled: boolean) => {
      if (!isDisabled) {
        handleCompanySelection(company);
        handleCloseSubmenu();
      }
    },
    [company, handleCompanySelection, handleCloseSubmenu],
  );

  const showCompanyActivationInfo = company.subscriptions.length === 0;
  const showCompanyApprovalInfo = company.userStatus === CompanyUserStatus.Unauthorized;

  const isDisabled = showCompanyActivationInfo || showCompanyApprovalInfo;

  const computedStyle = clsx(
    styles.selectableCompany,
    styles.automaticAddSecondCompany,
    { [styles.hovered!]: isHovered },
    { [styles.disabled!]: isDisabled },
  );

  return (
    <div
      className={computedStyle}
      {...hoverProps}
      onClick={() => {
        handleClick(isDisabled);
      }}
    >
      {isDisabled ? (
        <div className={styles.avatarContainer}>
          <IconPadlock className={styles.avatar} />
        </div>
      ) : (
        <CompanyAvatar company={company} round={true} />
      )}

      <VStack gap={0.5} className={styles.companyNameAndInfoContainer}>
        <Text variant="body2" weight="regular" ellipsis className={styles.name}>
          {company.name.split(' ').map(capitalize).join(' ')}
        </Text>
        {showCompanyActivationInfo && (
          <Text variant="caption" weight="medium" className={styles.infoMessage}>
            {t('company.submenu.activation_message', { ns: 'sidebar' })}
          </Text>
        )}
        {showCompanyApprovalInfo && !showCompanyActivationInfo && (
          <Text variant="caption" weight="medium" className={styles.infoMessage}>
            {t('company.submenu.approval_message', { ns: 'sidebar' })}
          </Text>
        )}
      </VStack>
    </div>
  );
};

const SelectableCompany: React.FC<{ company: Company; handleCloseSubmenu: () => void }> = ({
  company,
  handleCloseSubmenu,
}) => {
  const { hoverProps, isHovered } = useHover({});
  const switchCompany = useSwitchCompany();
  const { captureException } = useExceptionLogger();
  const { t } = useTranslation();

  const handleCompanySelection = useCallback(
    (company: Company) => {
      try {
        switchCompany(company, RouteConfig.AppRoot.buildLink());
      } catch (err) {
        captureException(err, {
          extra: {
            new_company_id: company.id,
          },
        });
        snack.error(t('COMPANY_AUTHENTICATION_FAILED'));
      }
    },
    [switchCompany, t, captureException],
  );

  const handleClick = useCallback(() => {
    handleCompanySelection(company);
    handleCloseSubmenu();
  }, [company, handleCompanySelection, handleCloseSubmenu]);

  const computedStyle = clsx(styles.selectableCompany, { [styles.hovered!]: isHovered });

  return (
    <div className={computedStyle} {...hoverProps} onClick={handleClick}>
      <CompanyAvatar company={company} round={true} />
      <Text variant="body2" weight="regular" ellipsis>
        {company.name.split(' ').map(capitalize).join(' ')}
      </Text>
    </div>
  );
};

const SubmenuContent: React.FC<{ handleCloseSubmenu: () => void }> = ({ handleCloseSubmenu }) => {
  const company = useActiveCompany();
  const userQuery = useLoggedUser(true);
  const { showModal } = useModal();
  const navigate = useNavigate();
  const { t } = useTranslation('sidebar');
  const hasAutomaticSecondCompanyOnboarding = useHasAutomaticSecondCompanyOnboarding();
  const { track } = useAnalytics();

  const handleLogout = useCallback(async () => {
    const result = await showModal({
      component: ConfirmDialog,
      props: {
        title: t('logout_sibill'),
        text: t('logout_message'),
      },
    });

    if (result.action !== 'CONFIRM') {
      return;
    }

    navigate(RouteConfig.Logout.buildLink());
  }, [navigate, showModal, t]);

  const selectableCompanies = (userQuery.data?.companies || []).filter((el) => el.id !== company.id);

  return (
    <div className={styles.submenuContentContainer}>
      <div
        className={clsx(styles.submenuCompanyInfoContainer, [
          { [styles.automaticAddSecondCompany!]: hasAutomaticSecondCompanyOnboarding },
        ])}
      >
        <Text variant="body1" weight="semibold" style={{ marginBottom: 2 }} ellipsis>
          {company.name.split(' ').map(capitalize).join(' ')}
        </Text>
        <Text variant="caption" weight="medium" secondaryColor>
          {company.taxNumber}
        </Text>
      </div>
      <div className={styles.submenuActionsContainer}>
        <div className={styles.submenuCompaniesListContainer}>
          <div
            className={clsx(styles.submenuAddCompany, [
              { [styles.automaticAddSecondCompany!]: hasAutomaticSecondCompanyOnboarding },
            ])}
          >
            <Text variant={'body2'} weight={'semibold'}>
              {t('company.submenu.companies')}
            </Text>
            <Button
              variant={hasAutomaticSecondCompanyOnboarding ? 'outlined' : 'link'}
              size="small"
              Icon={IconPlus}
              onClick={() => {
                track(AnalyticsEvents.SECOND_COMPANIES_CREATION_STARTED, { step: 'clicked' });
                showModal({
                  component: AddCompanyModal,
                  props: {},
                });
              }}
            >
              {t('company.submenu.add_company')}
            </Button>
          </div>
          <div className={clsx(styles.submenuSelectableCompanies)}>
            <div className={styles.submenuSelectableCompanyContainer}>
              {selectableCompanies.map((c) =>
                hasAutomaticSecondCompanyOnboarding ? (
                  <SelectableCompanyAutomaticOnboardingSecondCompany
                    company={c}
                    key={c.id}
                    handleCloseSubmenu={handleCloseSubmenu}
                  />
                ) : (
                  <SelectableCompany company={c} key={c.id} handleCloseSubmenu={handleCloseSubmenu} />
                ),
              )}
            </div>
          </div>
        </div>
        <div className={styles.submenuLogoutContainer}>
          <Button variant="outlined" size="small" Icon={IconLogout} onClick={handleLogout}>
            {t('logout')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export const SidebarCompany: React.FC = () => {
  const { user } = useAuth();
  const [isSubmenuOpen, setIsSubmenuOpen] = useState<boolean>(false);
  const hasAutomaticSecondCompanyOnboarding = useHasAutomaticSecondCompanyOnboarding();

  const computedSubmenuClassname = clsx(subMenuStyles.submenu, {
    [subMenuStyles.open!]: isSubmenuOpen,
    [styles.submenu!]: hasAutomaticSecondCompanyOnboarding,
  });

  const containerRef = useRef<HTMLDivElement>(null);

  const closeSubmenu = useCallback(() => {
    setIsSubmenuOpen(false);
  }, []);

  useOnClickOutside(containerRef, closeSubmenu);

  if (!user) {
    return null;
  }
  return (
    <div ref={containerRef}>
      <div className={computedSubmenuClassname}>
        <SubmenuContentContainer>
          {isSubmenuOpen && <SubmenuContent handleCloseSubmenu={closeSubmenu} />}
        </SubmenuContentContainer>
      </div>
      <div onClick={() => setIsSubmenuOpen(!isSubmenuOpen)}>
        <CurrentCompany isActive={isSubmenuOpen} />
      </div>
    </div>
  );
};
